import { useRef } from 'react'
import { VisuallyHidden } from '@/components/ui'
import { manifesto } from '@/content/site'
import { useReducedMotion } from '@/hooks/useReducedMotion'
import { cx } from '@/lib/cx'
import { EASE, gsap, ScrollTrigger, useGSAP } from '@/lib/gsap'
import styles from './Manifesto.module.css'

/**
 * The numbers row. Technor numerals count up softly when the row arrives (tabular figures,
 * with the final value's width reserved so nothing shifts), labels in Switzer. Screen
 * readers get each final value straight away; the counting copy is `aria-hidden`.
 */
export function Stats() {
  const rootRef = useRef<HTMLUListElement>(null)
  const reducedMotion = useReducedMotion()

  useGSAP(
    () => {
      const root = rootRef.current
      if (!root) return
      const items = gsap.utils.toArray<HTMLElement>('[data-stat]', root)
      if (reducedMotion) {
        gsap.from(items, {
          opacity: 0,
          duration: 0.3,
          ease: 'none',
          scrollTrigger: { trigger: root, start: 'top 88%', once: true },
        })
        return
      }

      const timeline = gsap.timeline({ paused: true })
      const restore: (() => void)[] = []
      items.forEach((item, index) => {
        const at = index * 0.18
        const rule = item.querySelector('[data-rule]')
        const count = item.querySelector<HTMLElement>('[data-count]')
        const value = Number(item.dataset.value ?? 0)
        timeline
          .fromTo(rule, { scaleX: 0 }, { scaleX: 1, duration: 1.6, ease: EASE.inOut }, at)
          .fromTo(
            item.querySelectorAll('[data-rise]'),
            { opacity: 0, y: 26 },
            { opacity: 1, y: 0, duration: 1.4, stagger: 0.12, ease: EASE.out },
            at + 0.1,
          )
        // Write into React's own text node, so React's tree stays intact.
        const text = count?.firstChild
        if (value > 0 && text) {
          const proxy = { value: 0 }
          text.nodeValue = '0'
          restore.push(() => {
            text.nodeValue = String(value)
          })
          timeline.to(
            proxy,
            {
              value,
              duration: 2.6,
              ease: 'power3.out',
              onUpdate: () => {
                text.nodeValue = String(Math.round(proxy.value))
              },
            },
            at + 0.15,
          )
        }
      })
      ScrollTrigger.create({
        trigger: root,
        start: 'top 82%',
        once: true,
        onEnter: () => timeline.play(),
      })
      return () => restore.forEach((fn) => fn())
    },
    { scope: rootRef, dependencies: [reducedMotion], revertOnUpdate: true },
  )

  return (
    <ul ref={rootRef} className={styles.stats} role="list">
      {manifesto.stats.map((stat) => {
        const wordSuffix = stat.suffix.startsWith(' ')
        return (
          <li key={stat.label} className={styles.stat} data-stat data-value={stat.value}>
            <span className={styles.statRule} data-rule aria-hidden="true" />
            <p className={styles.statValue} data-rise>
              <VisuallyHidden>{`${stat.value}${stat.suffix}`}</VisuallyHidden>
              <span className={styles.statNumber} aria-hidden="true">
                <span className={cx(styles.countStack, 'tabular')}>
                  <span className={styles.countGhost}>{stat.value}</span>
                  <span className={styles.count} data-count>
                    {stat.value}
                  </span>
                </span>
                {stat.suffix ? (
                  <span className={wordSuffix ? styles.suffixWord : styles.suffix}>
                    {stat.suffix.trim()}
                  </span>
                ) : null}
              </span>
            </p>
            <p className={styles.statLabel} data-rise>
              {stat.label}
            </p>
          </li>
        )
      })}
    </ul>
  )
}
